import React, { PropsWithChildren, ReactNode, useMemo } from "react";
import { Empty, List } from "antd";
import { getItemPropertyValue, Item } from "../../data-types";
import { defaultRenderItem, VpdListItem, VpdListItemLoading } from "./vpd-list-item";
import clsx from "clsx";
import styles from "./vpd-list.css";

const {
  ["container"]: containerClassName,
  ["list-style-default"]: listStyleDefaultClassName,
  ["list-style-primary"]: listStylePrimaryClassName,
} = styles as any;

export type VpdListStyle = "default" | "primary";
const LIST_STYLES: Record<VpdListStyle, string> = {
  default: listStyleDefaultClassName,
  primary: listStylePrimaryClassName,
};

export type VpdListLabels = {
  loadingText?: string;
  emptyText?: ReactNode;
};
const DEFAULT_LABELS: VpdListLabels = {
  loadingText: "Loading...",
  emptyText: "No items loaded.",
};

export type VpdListItemRenderFunction<T extends Item> = (
  item: T,
  index: number,
  itemKeyProperty: string,
  itemTextProperty: string
) => ReactNode;

export interface VpdListProps<T extends Item> {
  readonly itemKeyProperty: string;
  readonly itemTextProperty: string;
  items: T[];
  renderItem?: VpdListItemRenderFunction<T>;
  className?: string;
  loading?: boolean;
  split?: boolean;
  bordered?: boolean;
  listStyle?: VpdListStyle;
  labels?: VpdListLabels;
}

export const VpdList = <T extends Item>({
  itemKeyProperty,
  itemTextProperty,
  items,
  renderItem,
  className,
  loading,
  split,
  bordered,
  listStyle,
  labels,
}: PropsWithChildren<VpdListProps<T>>) => {
  const isEmpty = !loading && items.length === 0;
  const { loadingText, emptyText } = useMemo(
    () => Object.assign({}, DEFAULT_LABELS, labels),
    [labels]
  );

  return (
    <div className={clsx(containerClassName, className)}>
      <List
        key={itemKeyProperty}
        className={LIST_STYLES[listStyle || "default"]}
        dataSource={items}
        locale={{ emptyText }}
        split={split === true}
        bordered={bordered || false}
        rowKey={(item) => getItemPropertyValue(item, itemKeyProperty)?.toString() || "0"}
        renderItem={(item, index) =>
          (renderItem || defaultRenderItem)(item, index, itemKeyProperty, itemTextProperty)
        }
      >
        {loading && <VpdListItemLoading text={loadingText || ""} />}
        {isEmpty && <Empty description={emptyText} />}
      </List>
    </div>
  );
};

VpdList.Item = VpdListItem;
